import React, { useState, useRef } from 'react';
import { Upload, RefreshCw, X, Camera } from 'lucide-react';
import axios from 'axios';

const API = '';

export function ImageUploader({ currentImage, onUpload, label = "Upload Image" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload`, formData);
      onUpload(`${API}${data.url}`);
    } catch (e) {
      alert('Upload failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${dragging ? 'border-saffron bg-saffron/5' : 'border-earth/20 hover:border-saffron/40'}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
      data-testid="image-uploader"
    >
      {currentImage && (
        <img src={currentImage} alt="Preview" className="w-full h-32 object-cover rounded mb-3" />
      )}
      <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="text-xs font-sans text-saffron hover:text-saffron-dark transition-colors flex items-center gap-1 mx-auto"
      >
        {uploading ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
        {uploading ? 'Uploading...' : label}
      </button>
      <p className="text-[10px] text-earth/30 font-sans mt-1">Drag & drop or click to browse</p>
    </div>
  );
}

export function MultiImageUploader({ photos = [], onChange, label = "Product Photos" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    try {
      const { data } = await axios.post(`${API}/api/upload/multiple`, formData);
      const urls = data.map(d => `${API}${d.url}`);
      onChange(prev => [...prev, ...urls.filter(u => !prev.includes(u))]);
    } catch (e) {
      alert('Upload failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (url) => {
    onChange(prev => prev.filter(p => p !== url));
  };

  return (
    <div data-testid="multi-image-uploader">
      <label className="text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold mb-2 block">{label}</label>
      {photos.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          {photos.map((url, i) => (
            <div key={url} className="relative group">
              <img src={url} alt={`Photo ${i + 1}`} className="w-full h-20 object-cover rounded" />
              <button
                onClick={() => removePhoto(url)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-photo-${i}`}
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div
        className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors ${dragging ? 'border-saffron bg-saffron/5' : 'border-earth/20 hover:border-saffron/40'}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
      >
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="text-xs font-sans text-saffron hover:text-saffron-dark transition-colors flex items-center gap-1 mx-auto"
        >
          {uploading ? <RefreshCw size={12} className="animate-spin" /> : <Camera size={12} />}
          {uploading ? 'Uploading...' : 'Add Photos'}
        </button>
        <p className="text-[10px] text-earth/30 font-sans mt-1">Drag & drop multiple images or click to browse</p>
      </div>
    </div>
  );
}
